import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LogOut, BookOpen, MessageSquare, Send, Filter, Trash2, Edit, ClipboardList, Award, Video, Search } from 'lucide-react';
import { toast } from 'sonner';
import { NotificationBell } from '../components/NotificationBell';
import RichTextEditor from '../components/RichTextEditor';
import FilePickerInput from '../components/FilePickerInput';
import YouTubeEmbed from '../components/YouTubeEmbed';
import LessonFiles from '../components/LessonFiles';
import MessageFiles from '../components/MessageFiles';
import AttendancePage from '../components/AttendancePage';
import CompetenciesPage from '../components/CompetenciesPage';
import { OnlineLessonsPanel } from '../components/online/OnlineLessonsPanel';
import LessonsTab from './teacher/LessonsTab';
import BoardTab from './teacher/BoardTab';
import PrivateTab from './teacher/PrivateTab';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function TeacherDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [boardMessages, setBoardMessages] = useState([]);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(true);

  // Lesson form
  const [lessonDate, setLessonDate] = useState('');
  const [lessonDescription, setLessonDescription] = useState('');
  const [lessonFiles, setLessonFiles] = useState([]);

  // Edit lesson state
  const [editLessonOpen, setEditLessonOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editLessonDate, setEditLessonDate] = useState('');
  const [editLessonDescription, setEditLessonDescription] = useState('');
  const [editLessonNewFiles, setEditLessonNewFiles] = useState([]);
  const [editLessonFilesToRemove, setEditLessonFilesToRemove] = useState(new Set());

  // Edit message state
  const [editingMessage, setEditingMessage] = useState(null);
  const [editMessageOpen, setEditMessageOpen] = useState(false);
  const [editMessageContent, setEditMessageContent] = useState('');
  const [editMessageFilesToRemove, setEditMessageFilesToRemove] = useState(new Set());
  const [editMessageNewFiles, setEditMessageNewFiles] = useState([]);

  // Board message form
  const [boardContent, setBoardContent] = useState('');
  const [boardClassId, setBoardClassId] = useState('');
  const [boardFiles, setBoardFiles] = useState([]);

  // Private message form
  const [privateContent, setPrivateContent] = useState('');
  const [privateRecipient, setPrivateRecipient] = useState('');
  const [privateFiles, setPrivateFiles] = useState([]);

  // Filters
  const [lessonFilter, setLessonFilter] = useState('all');
  const [boardFilter, setBoardFilter] = useState('all');
  const [privateFilter, setPrivateFilter] = useState('all');
  const [messageSearch, setMessageSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [classesRes, messagesRes, recipientsRes] = await Promise.all([
        axios.get(`${API_URL}/classes`, { headers }),
        axios.get(`${API_URL}/messages`, { headers }),
        axios.get(`${API_URL}/messages/recipients`, { headers })
      ]);

      const teacherClasses = classesRes.data.filter(c =>
        user.class_ids.includes(c.class_id)
      );
      setClasses(teacherClasses);
      
      const allMessages = messagesRes.data;
      setBoardMessages(allMessages.filter(m => m.message_type === 'board'));
      setPrivateMessages(allMessages.filter(m => m.message_type === 'individual'));
      
      setRecipients(recipientsRes.data);

      if (teacherClasses.length > 0) {
        setSelectedClass(teacherClasses[0].class_id);
        setBoardClassId(teacherClasses[0].class_id);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  }, [token, user.class_ids]);

  const fetchLessons = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API_URL}/lessons?class_id=${selectedClass}`,
        { headers }
      );
      setLessons(response.data);
    } catch (error) {
      console.error('Failed to fetch lessons:', error);
    }
  }, [selectedClass, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedClass) {
      fetchLessons();
    }
  }, [selectedClass, fetchLessons]);

  const handleCreateLesson = async (e) => {
    e.preventDefault();

    if (!lessonDate || !lessonDescription) {
      toast.error('Моля, попълнете всички полета');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('class_id', selectedClass);
      formData.append('date', lessonDate);
      formData.append('description', lessonDescription);
      if (lessonFiles && lessonFiles.length > 0) {
        Array.from(lessonFiles).forEach(f => formData.append('file', f));
      }

      await axios.post(`${API_URL}/lessons`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      toast.success('Учебният запис е създаден успешно');
      setLessonDate('');
      setLessonDescription('');
      setLessonFiles([]);
      fetchLessons();
    } catch (error) {
      console.error('Failed to create lesson:', error);
      toast.error('Грешка при създаване на учебния запис');
    }
  };

  const handleSendBoardMessage = async () => {
    if (!boardContent.trim()) {
      toast.error('Моля, въведете съобщение');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('content', boardContent);
      formData.append('message_type', 'board');
      formData.append('class_id', boardClassId);
      
      if (boardFiles && boardFiles.length > 0) {
        Array.from(boardFiles).forEach(file => {
          formData.append('file', file);
        });
      }

      await axios.post(`${API_URL}/messages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      toast.success('Съобщението е изпратено');
      setBoardContent('');
      setBoardFiles([]);
      fetchData();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Грешка при изпращане на съобщението');
    }
  };

  const handleSendPrivateMessage = async () => {
    if (!privateContent.trim()) {
      toast.error('Моля, въведете съобщение');
      return;
    }

    if (!privateRecipient) {
      toast.error('Моля, изберете получател');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('content', privateContent);
      // "class:<class_id>" is our marker for a group broadcast to all students of the class
      const isGroup = privateRecipient.startsWith('class:');
      if (isGroup) {
        formData.append('message_type', 'group');
        formData.append('class_id', privateRecipient.slice('class:'.length));
      } else {
        formData.append('message_type', 'individual');
        formData.append('recipient_id', privateRecipient);
      }

      if (privateFiles && privateFiles.length > 0) {
        Array.from(privateFiles).forEach(file => {
          formData.append('file', file);
        });
      }

      await axios.post(`${API_URL}/messages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      toast.success(isGroup ? 'Груповото съобщение е изпратено' : 'Съобщението е изпратено');
      setPrivateContent('');
      setPrivateRecipient('');
      setPrivateFiles([]);
      fetchData();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Грешка при изпращане на съобщението');
    }
  };

  // handleDownloadMessageFile is now handled by MessageFiles component

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setEditLessonDate(lesson.date);
    setEditLessonDescription(lesson.description);
    setEditLessonNewFiles([]);
    setEditLessonFilesToRemove(new Set());
    setEditLessonOpen(true);
  };

  const handleUpdateLesson = async (e) => {
    e.preventDefault();
    if (!editLessonDate || !editLessonDescription) {
      toast.error('Моля попълнете датата и описанието');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('date', editLessonDate);
      formData.append('description', editLessonDescription);
      formData.append('remove_file_indices', Array.from(editLessonFilesToRemove).join(','));
      if (editLessonNewFiles.length > 0) {
        editLessonNewFiles.forEach(f => formData.append('file', f));
      }
      await axios.put(`${API_URL}/lessons/${editingLesson.lesson_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Записът е обновен успешно');
      setEditLessonOpen(false);
      setEditingLesson(null);
      fetchLessons();
    } catch (error) {
      toast.error('Грешка при обновяване на записа');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този учебен запис?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Учебният запис е изтрит успешно');
      fetchLessons();
    } catch (error) {
      console.error('Failed to delete lesson:', error);
      toast.error(error.response?.data?.detail || 'Грешка при изтриване на записа');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете това съобщение?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Съобщението е изтрито успешно');
      fetchData();
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error(error.response?.data?.detail || 'Грешка при изтриване на съобщението');
    }
  };

  const handleEditMessage = (msg) => {
    setEditingMessage(msg);
    setEditMessageContent(msg.content || '');
    setEditMessageFilesToRemove(new Set());
    setEditMessageNewFiles([]);
    setEditMessageOpen(true);
  };

  const handleUpdateMessage = async (e) => {
    e.preventDefault();
    if (!editMessageContent.trim()) { toast.error('Съобщението не може да е празно'); return; }
    try {
      const formData = new FormData();
      formData.append('content', editMessageContent);
      formData.append('remove_file_indices', Array.from(editMessageFilesToRemove).join(','));
      editMessageNewFiles.forEach(f => formData.append('file', f));
      const res = await axios.put(`${API_URL}/messages/${editingMessage.message_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Съобщението е обновено успешно');
      setEditMessageOpen(false);
      const updatedMsg = res.data;
      setBoardMessages(prev => prev.map(m => m.message_id === updatedMsg.message_id ? updatedMsg : m));
      setPrivateMessages(prev => prev.map(m => m.message_id === updatedMsg.message_id ? updatedMsg : m));
    } catch (error) {
      toast.error('Грешка при обновяване на съобщението');
    }
  };

  const getSenderRoleColor = (role) => {
    if (role === 'teacher') return 'bg-blue-100 text-blue-700';
    if (role === 'student') return 'bg-green-100 text-green-700';
    if (role === 'administrator') return 'bg-purple-100 text-purple-700';
    return 'bg-slate-100 text-slate-700';
  };

  const isImageFile = (filePath) => {
    if (!filePath) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    return imageExtensions.some(ext => filePath.toLowerCase().endsWith(ext));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredLessons = lessons.filter(lesson => {
    if (lessonFilter === 'all') return true;
    return true;
  });

  const _matchMsg = (msg) => {
    if (!messageSearch) return true;
    const needle = messageSearch.toLowerCase();
    const hay = `${msg.sender_name || ''} ${(msg.content || '').replace(/<[^>]*>/g, '')}`.toLowerCase();
    return hay.includes(needle);
  };

  const filteredBoardMessages = boardMessages.filter(msg => {
    if (boardFilter !== 'all') {
      if (boardFilter === 'student' && msg.sender_role !== 'student') return false;
      if (boardFilter === 'admin' && msg.sender_role !== 'administrator') return false;
    }
    return _matchMsg(msg);
  });

  const filteredPrivateMessages = privateMessages.filter(msg => {
    if (privateFilter !== 'all') {
      if (privateFilter === 'student' && msg.sender_role !== 'student') return false;
      if (privateFilter === 'teacher' && msg.sender_role !== 'teacher') return false;
      if (privateFilter === 'admin' && msg.sender_role !== 'administrator') return false;
    }
    return _matchMsg(msg);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Зареждане...</div>
      </div>
    );
  }

  const ctx = {
    user,
    token,
    classes,
    selectedClass,
    setSelectedClass,
    lessons,
    filteredLessons,
    lessonDate,
    setLessonDate,
    lessonDescription,
    setLessonDescription,
    lessonFiles,
    setLessonFiles,
    lessonFilter,
    setLessonFilter,
    handleCreateLesson,
    handleEditLesson,
    handleDeleteLesson,
    boardClassId,
    setBoardClassId,
    boardContent,
    setBoardContent,
    boardFiles,
    setBoardFiles,
    boardFilter,
    setBoardFilter,
    handleSendBoardMessage,
    boardMessages,
    filteredBoardMessages,
    privateRecipient,
    setPrivateRecipient,
    privateContent,
    setPrivateContent,
    privateFiles,
    setPrivateFiles,
    privateFilter,
    setPrivateFilter,
    handleSendPrivateMessage,
    privateMessages,
    filteredPrivateMessages,
    recipients,
    messageSearch,
    setMessageSearch,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8EDE2'}}>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          {/* Desktop header */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">БНУ Азбукарче</h1>
                <p className="text-sm text-slate-600">Учител: {getFullName(user)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell token={token} />
              <ChangePasswordDialog token={token} />
              <Button
                onClick={handleLogout}
                variant="outline"
                data-testid="logout-btn"
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Изход
              </Button>
            </div>
          </div>

          {/* Mobile header */}
          <div className="flex sm:hidden items-center justify-between">
            <div className="flex items-center gap-2">
              <div>
                <h1 className="text-lg font-bold text-slate-900">БНУ Азбукарче</h1>
                <p className="text-xs text-slate-600">{user.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <NotificationBell token={token} />
              <ChangePasswordDialog token={token} />
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="logout-btn"
                className="flex items-center gap-1"
              >
                <LogOut className="h-3 w-3" />
                <span className="text-xs">Изход</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Tabs defaultValue="lessons" className="space-y-6">
          <TabsList data-testid="teacher-tabs" className="flex flex-wrap w-full h-auto gap-1 p-1 bg-slate-100 rounded-lg">
            <TabsTrigger value="lessons" data-testid="lessons-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Записи</span>
            </TabsTrigger>
            <TabsTrigger value="board" data-testid="board-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <MessageSquare className="h-4 w-4" />
              <span>Табло</span>
            </TabsTrigger>
            <TabsTrigger value="private" data-testid="private-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <MessageSquare className="h-4 w-4" />
              <span>Лични</span>
            </TabsTrigger>
            <TabsTrigger value="attendance" data-testid="attendance-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <ClipboardList className="h-4 w-4" />
              <span>Присъствия</span>
            </TabsTrigger>
            <TabsTrigger value="competencies" data-testid="competencies-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <Award className="h-4 w-4" />
              <span>Комп.</span>
            </TabsTrigger>
            <TabsTrigger value="online" data-testid="online-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <Video className="h-4 w-4" />
              <span>Онлайн</span>
            </TabsTrigger>
          </TabsList>

          <LessonsTab ctx={ctx} />

          <BoardTab ctx={ctx} />

          <PrivateTab ctx={ctx} />

          <TabsContent value="attendance" data-testid="attendance-content">
            <AttendancePage classes={classes} token={token} />
          </TabsContent>

          <TabsContent value="competencies" data-testid="competencies-content">
            <CompetenciesPage classes={classes} token={token} />
          </TabsContent>

          <TabsContent value="online" data-testid="online-content">
            <OnlineLessonsPanel role="teacher" />
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Lesson Dialog */}
      <Dialog open={editLessonOpen} onOpenChange={setEditLessonOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Редактиране на запис</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateLesson} className="space-y-4">
            <div>
              <Label htmlFor="edit-lesson-date">Дата</Label>
              <Input
                id="edit-lesson-date"
                type="date"
                value={editLessonDate}
                onChange={(e) => setEditLessonDate(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Описание на урока</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={editLessonDescription}
                  onChange={setEditLessonDescription}
                  placeholder="Опишете съдържанието на урока..."
                />
              </div>
            </div>
            {editingLesson && (editingLesson.file_paths?.length > 0 || editingLesson.file_path) && (
              <div>
                <Label className="text-slate-600 text-sm">Съществуващи файлове</Label>
                <div className="mt-1 space-y-1.5">
                  {(editingLesson.file_paths?.length > 0 ? editingLesson.file_paths : [editingLesson.file_path]).map((fp, i) => {
                    const name = editingLesson.file_names?.[i] || editingLesson.file_name || 'файл';
                    const removed = editLessonFilesToRemove.has(i);
                    return (
                      <div key={fp || `file-${i}`} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${removed ? 'bg-red-50 border-red-200 line-through text-slate-400' : 'bg-slate-50 border-slate-200'}`}>
                        <span className="truncate">{name}</span>
                        <button type="button"
                          onClick={() => setEditLessonFilesToRemove(prev => {
                            const next = new Set(prev);
                            if (next.has(i)) next.delete(i); else next.add(i);
                            return next;
                          })}
                          className={`ml-2 text-xs flex-shrink-0 px-2 py-0.5 rounded ${removed ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                        >{removed ? 'Върни' : 'Премахни'}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label className="mb-2 block">Добави нови файлове (опционално)</Label>
              <FilePickerInput
                files={editLessonNewFiles}
                onChange={setEditLessonNewFiles}
                label="Добавете файлове"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setEditLessonOpen(false)}
              >
                Отказ
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#00966E] hover:bg-[#007a59] text-white"
              >
                Запази промените
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Message Edit Dialog */}
      <Dialog open={editMessageOpen} onOpenChange={setEditMessageOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Редактиране на съобщение</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateMessage} className="space-y-4">
            <div>
              <Label>Съдържание</Label>
              <div className="mt-1">
                <RichTextEditor value={editMessageContent} onChange={setEditMessageContent} placeholder="Въведете съобщение..." />
              </div>
            </div>
            {editingMessage?.file_paths?.length > 0 && (
              <div>
                <Label className="text-slate-600 text-sm">Прикачени файлове</Label>
                <div className="mt-1 space-y-1.5">
                  {editingMessage.file_paths.map((fp, i) => {
                    const name = editingMessage.file_names?.[i] || 'файл';
                    const removed = editMessageFilesToRemove.has(i);
                    return (
                      <div key={fp || `file-${i}`} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${removed ? 'bg-red-50 border-red-200 line-through text-slate-400' : 'bg-slate-50 border-slate-200'}`}>
                        <span className="truncate">{name}</span>
                        <button type="button"
                          onClick={() => setEditMessageFilesToRemove(prev => {
                            const next = new Set(prev);
                            if (next.has(i)) next.delete(i); else next.add(i);
                            return next;
                          })}
                          className={`ml-2 text-xs flex-shrink-0 px-2 py-0.5 rounded ${removed ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                        >{removed ? 'Върни' : 'Премахни'}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label className="mb-2 block">Добави нови файлове</Label>
              <FilePickerInput files={editMessageNewFiles} onChange={setEditMessageNewFiles} label="Прикачи файлове" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditMessageOpen(false)}>Отказ</Button>
              <Button type="submit" className="flex-1 bg-[#00966E] hover:bg-[#007a59] text-white">Запази</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
